import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Check, Gift, Sparkles } from 'lucide-react';
import { useCurrency } from '../hooks/useCurrency';
const bonuses = [{
  id: 1,
  title: 'The Body Back in 30 Reset Menu',
  image: 'https://cdn.magicpatterns.com/uploads/gj3HysKSw4wcCqLVvZKhfJ/bono1.png',
  description: 'When you finally get a window… the last thing you need is decision fatigue. This is a clean, pick-one menu of 12 simple options to use your 30 minutes on purpose—so you step back into the day lighter.',
  includes: ["12 'pick-1' reset options (shower, food, downshift, sunlight, stretch, etc.)", 'The simple 5–20–5 structure (land → reset → close)', "'Only got 10 minutes?' mini version", '1-minute Before/After check-in']
}, {
  id: 2,
  title: "The 'Someone Else's Arms' Handoff Guide",
  image: 'https://cdn.magicpatterns.com/uploads/htb81b6jSf8DQ4YtE2tBq8/bono2.png',
  description: "Support shouldn't require a 10-minute explanation… every time. This is the calm, one-sheet handoff system you can give to a grandparent, sitter, or friend—so you can step away for 30–60 minutes without spiraling.",
  includes: ['3 simple handoff sheets (different situations)', "'Before you leave the room' checklist", 'Ready-to-send texts to set expectations gently', 'Printable + phone-friendly version']
}, {
  id: 3,
  title: "The 'I Need Help' Text Script Kit",
  image: 'https://cdn.magicpatterns.com/uploads/gs7bBNjs5HcyXxCHdEFney/bono3.png',
  description: "Sometimes the hardest part isn't the nap. It's asking for help—without guilt, without over-explaining, without feeling like you have to justify it. This kit gives you 18 copy-paste messages you can send in 30 seconds.",
  includes: ['18 scripts (partner, grandparents, friend, sitter)', 'Simple message formula: time + task + duration + kind close', "Follow-ups that don't feel awkward", "'My non-negotiables' template (so your help actually helps)"]
}];
export function Bonuses() {
  const {
    formatPrice
  } = useCurrency();
  return <section className="py-12 md:py-20 px-6 md:px-8 bg-cream-dark relative overflow-hidden" data-id="element-2">
      {/* Subtle ambient glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-terracotta/5 rounded-full blur-3xl pointer-events-none" data-id="element-3" />

      <div className="max-w-4xl mx-auto relative" data-id="element-4">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} className="text-center mb-16 md:mb-24" data-id="element-5">
          {/* Premium badge */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="inline-flex items-center gap-2 bg-terracotta/10 border border-terracotta/20 rounded-full px-5 py-2 mb-6" data-id="element-6">
            <Gift className="w-5 h-5 text-terracotta" data-id="element-7" />
            <p className="text-sm uppercase tracking-[0.15em] text-terracotta font-bold" data-id="element-8">
              3 Exclusive Bonuses
            </p>
            <div className="w-px h-4 bg-terracotta/25" data-id="element-9" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-terracotta/80 font-bold animate-pulse" data-id="element-10">
              Today only
            </span>
            <div className="w-px h-4 bg-terracotta/25" data-id="element-11" />
            <p className="font-serif text-xl font-black uppercase tracking-wide" data-id="element-12">
              <span className="text-brown/80" data-id="element-13">{formatPrice(51)} Value</span>{' '}
              <span className="text-white bg-terracotta px-2 py-0.5 rounded-md text-base ml-1" style={{
              textShadow: '0 1px 4px rgba(198,114,76,0.4)'
            }} data-id="element-14">
                FREE
              </span>
            </p>
            <Sparkles className="w-4 h-4 text-terracotta animate-pulse" data-id="element-15" />
          </motion.div>

          <h2 className="font-serif text-3xl md:text-5xl text-brown mb-6 leading-tight" data-id="element-16">
            You're not just getting the Reset.
          </h2>
          <p className="text-lg md:text-xl text-brown-light max-w-2xl mx-auto leading-relaxed mb-4" data-id="element-17">
            You're getting the tools that make the 30 minutes{' '}
            <span className="italic" data-id="element-18">actually happen</span>—and feel good when
            they do.
          </p>
          <div className="inline-flex items-center gap-4 bg-terracotta/15 border-2 border-terracotta/30 rounded-full px-7 py-3.5 mt-2 shadow-md shadow-terracotta/10" data-id="element-19">
            <span className="font-serif text-lg text-brown/30 line-through italic" data-id="element-20">
              {formatPrice(51)} value
            </span>
            <div className="w-px h-6 bg-terracotta/25" data-id="element-21" />
            <span className="text-terracotta font-serif font-extrabold text-2xl uppercase tracking-wide drop-shadow-sm" data-id="element-22">
              FREE
            </span>
            <span className="text-terracotta/70 font-medium text-sm tracking-wider uppercase animate-pulse flex items-center gap-1.5" data-id="element-23">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-24">
                <path d="M12 12c-2-2.67-4-4-4-6a4 4 0 0 1 8 0c0 2-2 3.33-4 6Z" data-id="element-25" />
                <path d="M12 21a8 8 0 0 0 4.09-14.91" data-id="element-26" />
                <path d="M12 21a8 8 0 0 1-4.09-14.91" data-id="element-27" />
              </svg>
              today only
            </span>
          </div>
        </motion.div>

        {/* Bonus Cards */}
        <div className="space-y-12 md:space-y-20 mb-20" data-id="element-28">
          {bonuses.map((bonus, index) => <motion.div key={bonus.id} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.7,
          delay: index * 0.1,
          ease: 'easeOut'
        }} className="relative bg-white rounded-2xl md:rounded-3xl border border-brown/[0.06] overflow-hidden flex flex-col md:flex-row gap-0" style={{
          boxShadow: '0 1px 3px rgba(61,50,41,0.04), 0 8px 32px rgba(61,50,41,0.05), 0 24px 48px rgba(61,50,41,0.03)'
        }} data-id="element-29">
              {/* Top accent gradient strip */}
              <div className="h-[3px] w-full absolute top-0 left-0 right-0 z-10" style={{
            background: 'linear-gradient(90deg, transparent 10%, rgba(198,114,76,0.25) 50%, transparent 90%)'
          }} data-id="element-30" />

              {/* Image */}
              <div className="w-full md:w-2/5 shrink-0 relative" data-id="element-31">
                <div className="relative aspect-square md:aspect-auto md:h-full overflow-hidden bg-cream flex items-center justify-center p-4 md:p-6" data-id="element-32">
                  <img src={bonus.image} alt={bonus.title} loading="lazy" className="w-full h-full object-contain" data-id="element-33" />
                  <div className="absolute inset-0 bg-gradient-to-tr from-brown/8 via-transparent to-transparent pointer-events-none" data-id="element-34" />
                  {/* Warm inner glow */}
                  <div className="absolute bottom-0 left-0 right-0 h-1/3 pointer-events-none" style={{
                background: 'linear-gradient(to top, rgba(250,247,242,0.4), transparent)'
              }} data-id="element-35" />
                </div>
                {/* FREE badge on image */}
                <div className="absolute top-4 right-4 md:top-5 md:right-5 bg-terracotta text-white text-[10px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 rounded-full z-10" style={{
              boxShadow: '0 4px 12px rgba(198,114,76,0.3)'
            }} data-id="element-36">
                  Free
                </div>
              </div>

              {/* Content */}
              <div className="w-full md:w-3/5 p-6 md:p-8 lg:p-10" data-id="element-37">
                <div className="mb-5" data-id="element-38">
                  <div className="flex items-center gap-3 mb-3" data-id="element-39">
                    <span className="font-serif text-2xl md:text-3xl font-light text-brown/10 leading-none select-none" data-id="element-40">
                      0{bonus.id}
                    </span>
                    <div className="h-px flex-1 bg-gradient-to-r from-sage/15 to-transparent" data-id="element-41" />
                    <p className="text-[10px] font-bold tracking-[0.2em] text-sage uppercase" data-id="element-42">
                      Bonus
                    </p>
                  </div>
                  <h3 className="font-serif text-2xl md:text-3xl text-brown font-medium leading-tight" data-id="element-43">
                    {bonus.title}
                  </h3>
                </div>

                <p className="text-brown-light/80 leading-relaxed mb-6 text-[15px]" data-id="element-44">
                  {bonus.description}
                </p>

                <div className="space-y-3" data-id="element-45">
                  <p className="text-[10px] font-bold text-brown/50 uppercase tracking-[0.2em] mb-3" data-id="element-46">
                    What's included
                  </p>
                  {bonus.includes.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-47">
                      <div className="mt-0.5 w-5 h-5 rounded-full bg-sage/8 flex items-center justify-center shrink-0" style={{
                  boxShadow: 'inset 0 1px 3px rgba(139,152,119,0.1)'
                }} data-id="element-48">
                        <Check className="w-3 h-3 text-sage-dark" strokeWidth={2.5} data-id="element-49" />
                      </div>
                      <span className="text-brown-light/80 text-sm md:text-[15px] leading-snug" data-id="element-50">
                        {item}
                      </span>
                    </div>)}
                </div>
              </div>
            </motion.div>)}
        </div>

        {/* Bottom Summary */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} className="relative max-w-3xl mx-auto" data-id="element-51">
          {/* Summary card with elevated styling */}
          <div className="relative rounded-2xl md:rounded-3xl border border-brown/[0.06] overflow-hidden" style={{
          background: 'linear-gradient(160deg, #FFFFFF 0%, #FAF7F2 100%)',
          boxShadow: '0 1px 3px rgba(61,50,41,0.04), 0 8px 32px rgba(61,50,41,0.06), 0 24px 48px rgba(61,50,41,0.04)'
        }} data-id="element-52">
            {/* Top accent strip */}
            <div className="h-[3px] w-full" style={{
            background: 'linear-gradient(90deg, transparent 5%, rgba(198,114,76,0.3) 30%, rgba(139,152,119,0.2) 70%, transparent 95%)'
          }} data-id="element-53" />
            {/* Warm top glow */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2/3 h-32 pointer-events-none" style={{
            background: 'radial-gradient(ellipse at top, rgba(198,114,76,0.05) 0%, transparent 70%)'
          }} data-id="element-54" />

            <div className="p-8 md:p-12 relative" data-id="element-55">
              {/* Ornamental divider */}
              <div className="flex items-center justify-center gap-3 mb-4" data-id="element-56">
                <div className="w-6 h-px bg-gradient-to-r from-transparent to-terracotta/25" data-id="element-57" />
                <Sparkles className="w-3.5 h-3.5 text-terracotta/50" data-id="element-58" />
                <div className="w-6 h-px bg-gradient-to-l from-transparent to-terracotta/25" data-id="element-59" />
              </div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-terracotta font-bold text-center mb-3" data-id="element-60">
                Your Complete Bonus Stack
              </p>
              <h3 className="font-serif text-2xl md:text-3xl text-brown text-center mb-8 leading-snug" data-id="element-61">
                3 premium tools. All yours.{' '}
                <span className="text-terracotta italic" data-id="element-62">Free.</span>
              </h3>

              <div className="space-y-3 mb-8" data-id="element-63">
                {bonuses.map((bonus, i) => <div key={bonus.id} className="flex items-center gap-4 py-3.5 px-4 md:px-5 rounded-2xl bg-cream/60 border border-brown/[0.04]" style={{
                boxShadow: '0 1px 4px rgba(61,50,41,0.02), 0 4px 12px rgba(61,50,41,0.02)'
              }} data-id="element-64">
                    <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl overflow-hidden shrink-0 border border-brown/[0.06]" style={{
                  boxShadow: '0 2px 8px rgba(61,50,41,0.06)'
                }} data-id="element-65">
                      <img src={bonus.image} alt={bonus.title} loading="lazy" className="w-full h-full object-cover" data-id="element-66" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-67">
                      <span className="text-brown font-medium text-sm md:text-base block truncate leading-tight" data-id="element-68">
                        {bonus.title}
                      </span>
                      <span className="text-brown-light/50 text-[11px]" data-id="element-69">
                        Bonus #{bonus.id}
                      </span>
                    </div>
                    <div className="shrink-0 bg-sage/8 text-sage-dark text-[10px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 rounded-full" style={{
                  boxShadow: 'inset 0 1px 3px rgba(139,152,119,0.08)'
                }} data-id="element-70">
                      Free
                    </div>
                  </div>)}
              </div>

              <div className="pt-8 border-t border-brown/[0.05]" data-id="element-71">
                <div className="flex flex-col items-center gap-3" data-id="element-72">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-brown/35 font-bold" data-id="element-73">
                    Total bonus value
                  </p>
                  <div className="flex items-baseline gap-4" data-id="element-74">
                    <span className="font-serif text-xl text-brown/20 line-through italic" data-id="element-75">
                      {formatPrice(51)}
                    </span>
                    <div className="w-px h-6 bg-terracotta/15" data-id="element-76" />
                    <span className="font-serif text-5xl md:text-6xl text-terracotta font-bold tracking-tight" style={{
                    textShadow: '0 2px 24px rgba(198,114,76,0.2)'
                  }} data-id="element-77">
                      FREE
                    </span>
                  </div>
                  <p className="font-serif text-sm italic text-brown/45 mt-1" data-id="element-78">
                    Included with your Reset —{' '}
                    <span className="text-terracotta font-semibold not-italic" data-id="element-79">
                      only today
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}